import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, Check } from "lucide-react";
import NavBar from "../components/NavBar";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inp = "w-full px-3 py-2.5 bg-[#F5F3ED] border border-[#E5E1D8] rounded-md text-sm focus:outline-none focus:border-[#8C6C50] transition-colors";

const AREAS = [
  { v: "centre-ville", l: "Centre-ville" },
  { v: "petite-couronne", l: "Petite couronne" },
  { v: "grande-couronne", l: "Grande couronne" },
];
const TYPES = [
  { v: "studio", l: "Studio" },
  { v: "t2", l: "2 pièces" },
  { v: "t3", l: "3 pièces" },
  { v: "t4plus", l: "4 pièces et plus" },
];

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs tracking-[0.15em] uppercase font-semibold text-[#666]">
        {label}{required && <span className="text-[#8C6C50]"> *</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Choice({ options, values, onToggle, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const on = values.includes(o.v);
        return (
          <button
            key={o.v}
            type="button"
            aria-pressed={on}
            onClick={() => onToggle(o.v)}
            className={`px-4 py-2 rounded-full text-sm border transition-colors inline-flex items-center gap-1.5 ${
              on ? "bg-[#8C6C50] border-[#8C6C50] text-white" : "bg-white border-[#E5E1D8] text-[#2B3029] hover:border-[#8C6C50]"
            }`}
            data-testid={`${testid}-${o.v}`}
          >
            {on && <Check className="w-3.5 h-3.5" />}{o.l}
          </button>
        );
      })}
    </div>
  );
}

function Success({ onReset }) {
  return (
    <div className="bg-white border border-[#E5E1D8] rounded-md p-10 text-center" data-testid="custom-request-success">
      <CheckCircle2 className="w-10 h-10 text-[#8C6C50] mx-auto" />
      <h2 className="font-serif text-3xl text-[#1A1A1A] mt-4">Demande envoyée</h2>
      <p className="text-sm text-[#666] mt-3 max-w-md mx-auto">
        Merci ! Nous recherchons un hébergement à tarif négocié correspondant à vos critères et revenons vers vous rapidement.
      </p>
      <div className="mt-8 flex justify-center gap-4 flex-wrap">
        <button onClick={onReset} className="px-5 py-2.5 rounded-full border border-[#E5E1D8] text-sm hover:bg-[#F5F3ED]" data-testid="custom-request-new-btn">
          Nouvelle demande
        </button>
        <Link to="/" className="btn-primary px-5 py-2.5 rounded-full text-sm font-medium">Retour à l'accueil</Link>
      </div>
    </div>
  );
}

export default function CustomRequest() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const today = new Date().toISOString().slice(0, 10);
  const initial = {
    city: params.get("city") || "", areas: [], area_details: "", arrival_date: "", duration_months: 3,
    apartment_types: [], guests: Number(params.get("guests")) || 1,
    budget_max: params.get("budget") || "", name: user?.name || "", email: user?.email || "",
    phone: "", message: "", website: "",
  };
  const [v, setV] = useState(initial);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));
  const toggle = (k, x) => setV((s) => ({ ...s, [k]: s[k].includes(x) ? s[k].filter((y) => y !== x) : [...s[k], x] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!v.apartment_types.length) {
      toast.error("Choisissez au moins un type d'appartement");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/custom-requests`, {
        ...v,
        duration_months: Number(v.duration_months),
        guests: Number(v.guests),
        budget_max: Number(v.budget_max),
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Merci de vérifier les champs du formulaire");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F3ED]">
      <NavBar />
      <section className="max-w-3xl mx-auto px-6 lg:px-10 pt-14 pb-24">
        <div className="text-xs tracking-[0.25em] uppercase font-semibold text-[#8C6C50]">Demande sur mesure</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-4 text-[#1A1A1A] leading-[1.05]">
          Décrivez votre séjour,<br />on s'occupe du reste.
        </h1>
        <p className="mt-6 text-base text-[#666] max-w-xl">
          Indiquez vos critères : nous sollicitons nos résidences partenaires pour vous proposer un logement clé en main à tarif négocié.
        </p>

        <div className="mt-12">
          {done ? (
            <Success onReset={() => { setV(initial); setDone(false); }} />
          ) : (
            <form onSubmit={submit} className="bg-white border border-[#E5E1D8] rounded-md p-6 sm:p-10 space-y-8" data-testid="custom-request-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Ville" required>
                  <input required minLength={2} value={v.city} onChange={(e) => set("city", e.target.value)} placeholder="Ex. Lyon" className={inp} data-testid="cr-city-input" />
                </Field>
                <Field label="Date d'arrivée" required>
                  <input required type="date" min={today} value={v.arrival_date} onChange={(e) => set("arrival_date", e.target.value)} className={inp} data-testid="cr-arrival-input" />
                </Field>
              </div>
              <Field label="Emplacement (optionnel, plusieurs choix possibles)">
                <Choice options={AREAS} values={v.areas} onToggle={(x) => toggle("areas", x)} testid="cr-area" />
                <input
                  maxLength={300}
                  value={v.area_details}
                  onChange={(e) => set("area_details", e.target.value)}
                  placeholder="Précisez si besoin : quartier, proximité d'un lieu de travail, d'une gare…"
                  className={inp + " mt-3"}
                  data-testid="cr-area-details-input"
                />
              </Field>
              <Field label="Type d'appartement (plusieurs choix possibles)" required>
                <Choice options={TYPES} values={v.apartment_types} onToggle={(x) => toggle("apartment_types", x)} testid="cr-type" />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <Field label="Durée (mois)" required>
                  <input required type="number" min={1} max={36} value={v.duration_months} onChange={(e) => set("duration_months", e.target.value)} className={inp} data-testid="cr-duration-input" />
                </Field>
                <Field label="Personnes" required>
                  <input required type="number" min={1} max={20} value={v.guests} onChange={(e) => set("guests", e.target.value)} className={inp} data-testid="cr-guests-input" />
                </Field>
                <Field label="Budget max (€/mois)" required>
                  <input required type="number" min={1} value={v.budget_max} onChange={(e) => set("budget_max", e.target.value)} placeholder="Ex. 1200" className={inp} data-testid="cr-budget-input" />
                </Field>
              </div>

              <div className="pt-8 border-t border-[#E5E1D8] grid grid-cols-1 md:grid-cols-3 gap-6">
                <Field label="Nom" required>
                  <input required minLength={2} value={v.name} onChange={(e) => set("name", e.target.value)} className={inp} data-testid="cr-name-input" />
                </Field>
                <Field label="Email" required>
                  <input required type="email" value={v.email} onChange={(e) => set("email", e.target.value)} className={inp} data-testid="cr-email-input" />
                </Field>
                <Field label="Téléphone">
                  <input type="tel" value={v.phone} onChange={(e) => set("phone", e.target.value)} className={inp} data-testid="cr-phone-input" />
                </Field>
              </div>
              <Field label="Message (optionnel)">
                <textarea rows={4} maxLength={2000} value={v.message} onChange={(e) => set("message", e.target.value)} placeholder="Précisions utiles : motif du séjour, besoins particuliers…" className={inp} data-testid="cr-message-input" />
              </Field>
              <input type="text" tabIndex={-1} autoComplete="off" value={v.website} onChange={(e) => set("website", e.target.value)} className="hidden" aria-hidden="true" />

              <button type="submit" disabled={sending} className="btn-primary px-7 py-3.5 rounded-full text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60" data-testid="cr-submit-btn">
                {sending ? "Envoi…" : "Envoyer ma demande"} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
